// frontend/src/pages/Invoice.jsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { fmtDate as fmtTz } from '../utils/time';

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const round2 = (n) => Number((Math.round(n * 100) / 100).toFixed(2));
const fmtMoney = (n) => Number(toNum(n)).toFixed(2);

/* -------------------- Friendly alert banner -------------------- */
function Alert({ message, onClose }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        marginTop: 12,
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid #fecaca',
        background: '#fff1f2',
        color: '#b91c1c',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>⚠ {message}</div>
        <button className="btn btn-sm secondary" onClick={onClose} aria-label="Dismiss">
          Dismiss
        </button>
      </div>
    </div>
  );
}

/* --------- Open API PDF safely; map backend errors to UX text --------- */
async function openPdfSafe(apiPath, setUiError) {
  try {
    const resp = await api.get(apiPath, { responseType: 'blob' });
    const blob =
      resp?.data instanceof Blob
        ? resp.data
        : new Blob([resp?.data ?? ''], { type: resp?.headers?.['content-type'] || 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    let data = err?.response?.data;
    try {
      if (data instanceof Blob) {
        const txt = await data.text();
        try {
          data = JSON.parse(txt);
        } catch {
          data = { error: txt };
        }
      }
    } catch {}
    const error = data?.error || 'Unable to open document.';
    const missing = Array.isArray(data?.missing) ? data.missing[0] : undefined;

    let msg = 'Unable to open document.';
    if (error === 'Forbidden' && missing) {
      if (missing === 'invoices:export') msg = 'You don’t have permission to view or export invoices.';
      else if (missing === 'credit:export') msg = 'You don’t have permission to view or export credit notes.';
      else msg = 'You don’t have permission to view this document.';
    } else if (typeof error === 'string' && error.toLowerCase().includes('no returns')) {
      msg = 'No return entries were found for this invoice.';
    } else if (typeof error === 'string') {
      msg = error;
    }
    setUiError(msg);
  }
}

/** Derive per-line discount/net like SalesDetail.jsx */
function deriveLine(it) {
  const qty = toNum(it?.qty);
  const price = toNum(it?.price); // unit before discount
  const gstRate = toNum(it?.gst_rate ?? it?.gstRate);

  // any one of these may be present
  const pct = toNum(it?.discount_pct, NaN);
  const net = toNum(it?.net_price, NaN);
  const discPerItem = toNum(it?.discount_per_item, NaN);

  let perItemDisc;
  let netUnit;

  if (!Number.isNaN(pct)) {
    perItemDisc = round2(price * Math.max(0, Math.min(100, pct)) / 100);
    netUnit = round2(price - perItemDisc);
  } else if (!Number.isNaN(net)) {
    netUnit = round2(net);
    perItemDisc = round2(price - netUnit);
  } else if (!Number.isNaN(discPerItem)) {
    perItemDisc = round2(discPerItem);
    netUnit = round2(price - perItemDisc);
  } else {
    perItemDisc = 0;
    netUnit = price;
  }

  // line-level total discount may be given
  const lineDiscountGiven = it && Number.isFinite(Number(it.discount)) ? toNum(it.discount) : null;
  const lineDiscount = lineDiscountGiven != null ? toNum(lineDiscountGiven) : round2(qty * perItemDisc);

  const lineSubtotal = round2(qty * price);
  const lineNetBase = round2(lineSubtotal - lineDiscount);
  const lineGst = round2(lineNetBase * gstRate / 100);
  const lineIncl = round2(lineNetBase + lineGst);

  return { lineSubtotal, lineDiscount, lineGst, lineIncl };
}

/** Compute grand total at the moment of sale from items */
function grandFromItems(items = []) {
  if (!Array.isArray(items) || items.length === 0) return null;
  let subtotal = 0,
    discount = 0,
    gstTotal = 0;
  for (const it of items) {
    const d = deriveLine(it);
    subtotal += d.lineSubtotal;
    discount += d.lineDiscount;
    gstTotal += d.lineGst;
  }
  subtotal = round2(subtotal);
  discount = round2(discount);
  gstTotal = round2(gstTotal);
  return round2(subtotal - discount + gstTotal);
}

/** Compute from DB fields when present (ignores any credit/refund fields) */
function grandFromRowFields(r) {
  const hasAll =
    r != null && ['subtotal', 'discount', 'cgst', 'sgst', 'igst'].every((k) => r[k] != null && Number.isFinite(Number(r[k])));
  if (!hasAll) return null;
  const subtotal = toNum(r.subtotal);
  const discount = toNum(r.discount);
  const cgst = toNum(r.cgst);
  const sgst = toNum(r.sgst);
  const igst = toNum(r.igst);
  return round2(subtotal - discount + cgst + sgst + igst);
}

export default function Invoice() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('date_desc');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // UX error for PDF attempts
  const [uiError, setUiError] = useState('');

  // pagination (client-side)
  const [page, setPage] = useState(1); // 1-based
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const v = Number(localStorage.getItem('sales.rowsPerPage'));
    return [10, 25, 50, 100].includes(v) ? v : 25;
  });

  // load data
  async function load(q = '', srt = 'date_desc') {
    const params = {};
    if (q && q.trim()) params.search = q.trim();
    if (srt) params.sort = srt;
    if (fromDate) params.from = fromDate; // "YYYY-MM-DD"
    if (toDate) params.to = toDate;
    params.limit = 'all'; // fetch all, paginate on client

    const resp = await api.get('/sales', { params });
    const rows = Array.isArray(resp.data) ? resp.data : [];
    // Ensure items parsed if backend sends JSON string
    for (const r of rows) {
      if (typeof r.items === 'string') {
        try {
          r.items = JSON.parse(r.items);
        } catch {}
      }
    }
    setList(rows);
  }

  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    const t = setTimeout(() => load(search, sort), 300);
    return () => clearTimeout(t);
  }, [search, sort, fromDate, toDate]);
  useEffect(() => {
    setPage(1);
  }, [search, sort, fromDate, toDate]);

  // pagination derived values
  const total = list?.length || 0;
  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / rowsPerPage)), [total, rowsPerPage]);
  useEffect(() => {
    const maxPage = pageCount;
    if (page > maxPage) setPage(maxPage);
  }, [page, pageCount]);

  const onChangeRowsPerPage = (e) => {
    const v = Number(e.target.value || 25);
    localStorage.setItem('sales.rowsPerPage', String(v));
    setRowsPerPage(v);
    setPage(1);
  };

  const startIdx = (page - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const pageRows = useMemo(() => list.slice(startIdx, endIdx), [list, startIdx, endIdx]);

  const gotoFirst = () => setPage(1);
  const gotoPrev = () => setPage((p) => Math.max(1, p - 1));
  const gotoNext = () => setPage((p) => Math.min(pageCount, p + 1));
  const gotoLast = () => setPage(pageCount);

  // Safely compute "Grand Total (at sale)"
  const computeGrandAtSale = (r) => {
    // 1) Prefer computation from items when available (matches SalesDetail.jsx logic)
    const itemsGrand = grandFromItems(r?.items);
    if (itemsGrand != null) return itemsGrand;

    // 2) If row has all invoice fields, compute from them
    const fieldsGrand = grandFromRowFields(r);
    if (fieldsGrand != null) return fieldsGrand;

    // 3) Fallback to r.total (note: some backends may send 'after return' here,
    // but if the previous two methods were unavailable, this is the best we have)
    return toNum(r?.total);
  };

  return (
    <div className="container">
      {/* Friendly error banner for any failed PDF attempts */}
      <Alert message={uiError} onClose={() => setUiError('')} />

      <div className="toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ marginRight: 'auto' }}>Invoice</h2>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search invoice, customer, phone, GSTIN"
          style={{
            padding: 10,
            boxSizing: 'border-box',
            flex: '1 1 600px',
            minWidth: 360,
          }}
        />

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <label style={{ fontSize: 12 }}>From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <label style={{ fontSize: 12 }}>To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          {(fromDate || toDate) && (
            <button
              className="btn"
              onClick={() => {
                setFromDate('');
                setToDate('');
              }}
            >
              Clear
            </button>
          )}
        </div>

        <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ padding: 8, flexShrink: 0 }}>
          <option value="date_desc">Date: Newest → Oldest</option>
          <option value="date_asc">Date: Oldest → Newest</option>
          <option value="customer_asc">Customer: A → Z</option>
          <option value="customer_desc">Customer: Z → A</option>
          <option value="total_desc">Total: High → Low</option>
          <option value="total_asc">Total: Low → High</option>
        </select>
      </div>

      <div className="card" style={{ overflowX: 'auto', marginTop: '12px' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Phone</th>
              <th>Total</th>
              <th>Payment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => {
              const grandAtSale = computeGrandAtSale(r);
              return (
                <tr key={r.id}>
                  <td>{r.invoice_no}</td>
                  <td>{fmtTz(r.created_at || r.sold_at)}</td>
                  <td>{r.customer_name || '-'}</td>
                  <td>{r.customer_phone || '-'}</td>
                  <td>{fmtMoney(grandAtSale)}</td>
                  <td>{r.payment_method || '-'}</td>
                  <td>
                    <button
                      className="btn btn-sm"
                      onClick={() => openPdfSafe(`/sales/${encodeURIComponent(r.id)}/pdf`, setUiError)}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              );
            })}
            {!list.length && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>
                  No sales
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pager */}
        <div
          className="flex"
          style={{
            alignItems: 'center',
            gap: 12,
            justifyContent: 'space-between',
            padding: '8px 12px',
            borderTop: '1px solid var(--border, #e5e7eb)',
            flexWrap: 'wrap'
          }}
        >
          <div className="flex" style={{ alignItems: 'center', gap: 8 }}>
            <label className="muted" htmlFor="rows-per-page">
              Rows/page
            </label>
            <select
              id="rows-per-page"
              className="input"
              value={rowsPerPage}
              onChange={onChangeRowsPerPage}
              style={{ width: 96 }}
              aria-label="Rows per page"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>

            <span className="muted" style={{ marginLeft: 12 }}>
              {total ? `${startIdx + 1}–${Math.min(endIdx, total)} of ${total}` : '0 of 0'}
            </span>
          </div>

          <div className="flex" style={{ alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <button className="btn btn-sm secondary" onClick={gotoFirst} disabled={page <= 1} aria-label="First page">
              «
            </button>
            <button className="btn btn-sm secondary" onClick={gotoPrev} disabled={page <= 1} aria-label="Previous page">
              ‹
            </button>
            <span className="muted" style={{ padding: '0 6px' }}>
              Page <b>{page}</b> / {pageCount}
            </span>
            <button className="btn btn-sm secondary" onClick={gotoNext} disabled={page >= pageCount} aria-label="Next page">
              ›
            </button>
            <button className="btn btn-sm secondary" onClick={gotoLast} disabled={page >= pageCount} aria-label="Last page">
              »
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
